import React from 'react';
import { HelpCircle } from 'lucide-react';
import { ValuationData, ValidationErrors } from '../../../types/valuation';
import { Tooltip } from '../../ui/Tooltip';

interface ProfitCentersProps {
  data: ValuationData;
  onUpdate: (data: Partial<ValuationData>) => void;
  errors?: ValidationErrors;
}

// Define services for left and right columns to ensure specific ordering
const leftColumnServices = [
  'ABC Sales',
  'Lottery',
  'ATM',
  'Propane Exchange',
];

const rightColumnServices = [
  'Auto Repair',
  'Oil Change',
  'Tire Service',
  'EV Charging',
  'Truck Parking',
  'Vacuum Services',
  'Storage Units'
];

// Combine for backwards compatibility
const additionalServices = [...leftColumnServices, ...rightColumnServices];

export default function ProfitCenters({ data, onUpdate, errors }: ProfitCentersProps) {
  const [isAnnual, setIsAnnual] = React.useState(false);

  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const isAnnualSelected = e.target.value === 'annual';
    setIsAnnual(isAnnualSelected);
    // No need to convert values when switching periods since they're already stored as annual
  };

  const handleFinancialInputChange = (field: keyof ValuationData, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = parseInt(value.replace(/,/g, ''), 10);
    const finalValue = isNaN(numericValue) ? 0 : (isAnnual ? numericValue : numericValue * 12);
    onUpdate({ [field]: finalValue });
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
        <p className="text-sm text-slate-700">
          Select any additional services your station offers beyond fuel and convenience store sales. Each profit center adds value to the business — stations with diversified income streams typically command higher multiples.
        </p>
      </div>
      {/* Reporting Period - hidden but kept for backward compatibility */}
      <div className="hidden">
        <div className="flex items-center gap-2 mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Reporting Period
          </label>
          <select
            value={isAnnual ? 'annual' : 'monthly'}
            onChange={handlePeriodChange}
            className="ml-2 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
      </div>


      {/* Additional Services */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Additional Services
          </label>
          <Tooltip content="Select any additional services offered at the property">
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </Tooltip>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {/* Left column with specific ordering */}
          <div className="space-y-3">
            {leftColumnServices.map((service) => (
              <div key={service} className="flex items-center">
                <input
                  type="checkbox"
                  id={service}
                  checked={data.additionalServices?.includes(service) || false}
                  onChange={(e) => {
                    const currentServices = data.additionalServices || [];
                    const updatedServices = e.target.checked
                      ? [...currentServices, service]
                      : currentServices.filter((s) => s !== service);
                    onUpdate({ additionalServices: updatedServices });
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor={service} className="ml-2 block text-sm text-gray-700">
                  {service}
                </label>
              </div>
            ))}
          </div>
          
          {/* Right column */}
          <div className="space-y-3">
            {rightColumnServices.map((service) => (
              <div key={service} className="flex items-center">
              <input
                type="checkbox"
                id={service}
                checked={data.additionalServices?.includes(service) || false}
                onChange={(e) => {
                  const currentServices = data.additionalServices || [];
                  const updatedServices = e.target.checked
                    ? [...currentServices, service]
                    : currentServices.filter((s) => s !== service);
                  onUpdate({ additionalServices: updatedServices });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor={service} className="ml-2 block text-sm text-gray-700">
                {service}
              </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}